import { KeyboardEvent, PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FlaskScale } from './flask.model'

type UseFlaskDragParams = {
  value: number
  scale: FlaskScale
  onChange: (value: number) => void
  onCommit?: (value: number) => void
  minVisualPct?: number
  maxVisualPct?: number
  handleOnly?: boolean
}

export const FLASK_HANDLE_ATTR = 'data-flask-handle'

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

// R$ value → vertical position on the bar, as a percentage (0–100).
export const valueToPositionPct = (value: number, scale: FlaskScale): number => {
  const { min, max, breakpoint, splitPct } = scale
  const clamped = clamp(value, min, max)

  if (clamped <= breakpoint) {
    const span = breakpoint - min
    return span > 0 ? ((clamped - min) / span) * splitPct : 0
  }

  const span = max - breakpoint
  return span > 0 ? splitPct + ((clamped - breakpoint) / span) * (100 - splitPct) : splitPct
}

// Bar position (0–100%) → raw R$ value (before snapping).
export const positionPctToValue = (pct: number, scale: FlaskScale): number => {
  const { min, max, breakpoint, splitPct } = scale
  const clamped = clamp(pct, 0, 100)

  if (clamped <= splitPct) {
    return splitPct > 0 ? min + (clamped / splitPct) * (breakpoint - min) : min
  }

  return breakpoint + ((clamped - splitPct) / (100 - splitPct)) * (max - breakpoint)
}

// Snap to the step of the segment the value falls in, then clamp to [min, max].
export const snapValue = (raw: number, scale: FlaskScale): number => {
  const { min, max, breakpoint, stepLow, stepHigh } = scale
  const clamped = clamp(raw, min, max)

  if (clamped <= breakpoint) {
    return clamp(min + Math.round((clamped - min) / stepLow) * stepLow, min, breakpoint)
  }

  return clamp(breakpoint + Math.round((clamped - breakpoint) / stepHigh) * stepHigh, breakpoint, max)
}

// Arrow-key step: fine below the breakpoint, coarse at/above. Direction-aware at the
// boundary so the breakpoint steps up by stepHigh and down by stepLow.
export const stepValue = (value: number, direction: 1 | -1, scale: FlaskScale): number => {
  const { breakpoint, stepLow, stepHigh } = scale
  const step = direction > 0 ? (value < breakpoint ? stepLow : stepHigh) : value <= breakpoint ? stepLow : stepHigh

  return snapValue(value + direction * step, scale)
}

export const useFlask = ({
  value,
  scale,
  onChange,
  onCommit,
  minVisualPct = 0,
  maxVisualPct = 100,
  handleOnly = false
}: UseFlaskDragParams) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  // Continuous water position (band %) while dragging, so the surface glides with the
  // pointer instead of snapping between value steps. null when not dragging.
  const [dragVisualPct, setDragVisualPct] = useState<number | null>(null)
  const pointerIdRef = useRef<number | null>(null)
  const keyboardDirtyRef = useRef(false)

  // Latest value, read at commit time without re-binding window listeners.
  const valueRef = useRef(value)
  valueRef.current = value

  // Depend on the scale's fields rather than its identity, so callers may build the
  // scale inline without re-binding every callback below on each render.
  const { min, max, breakpoint, splitPct, stepLow, stepHigh } = scale
  const activeScale = useMemo(
    () => ({ min, max, breakpoint, splitPct, stepLow, stepHigh }),
    [min, max, breakpoint, splitPct, stepLow, stepHigh]
  )

  const resolveFromClientY = useCallback(
    (clientY: number) => {
      const track = trackRef.current
      if (!track) return null

      const rect = track.getBoundingClientRect()
      if (!rect.height) return null // pré-layout / jsdom → evita divisão por zero (NaN/Infinity)

      const fromBottom = rect.height - (clientY - rect.top)
      // Only the band between minFrac (floor = min) and maxFrac (cap = max) is usable.
      const minFrac = clamp(minVisualPct / 100, 0, 0.98)
      const maxFrac = clamp(maxVisualPct / 100, minFrac + 0.01, 1)
      const usableRatio = (fromBottom / rect.height - minFrac) / (maxFrac - minFrac)
      const ratio = clamp(usableRatio, 0, 1)

      // The snapped value drives the readout; the continuous position keeps the water
      // surface gliding with the pointer instead of jumping between snapped steps.
      return {
        value: snapValue(positionPctToValue(ratio * 100, activeScale), activeScale),
        visualPct: minVisualPct + ratio * (maxVisualPct - minVisualPct)
      }
    },
    [minVisualPct, maxVisualPct, activeScale]
  )

  const moveTo = useCallback(
    (clientY: number) => {
      const resolved = resolveFromClientY(clientY)
      if (!resolved) return

      setDragVisualPct(resolved.visualPct)
      if (resolved.value !== valueRef.current) onChange(resolved.value)
    },
    [resolveFromClientY, onChange]
  )

  const endDrag = useCallback(() => {
    setIsDragging(false)
    setDragVisualPct(null)
    pointerIdRef.current = null
    onCommit?.(valueRef.current)
  }, [onCommit])

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (handleOnly && !(event.target as Element).closest?.(`[${FLASK_HANDLE_ATTR}]`)) return

      event.preventDefault()
      pointerIdRef.current = event.pointerId
      setIsDragging(true)
      event.currentTarget.setPointerCapture?.(event.pointerId)
      moveTo(event.clientY)
    },
    [handleOnly, moveTo]
  )

  useEffect(() => {
    if (!isDragging) return undefined

    const isActivePointer = (event: globalThis.PointerEvent) =>
      pointerIdRef.current === null || event.pointerId === pointerIdRef.current

    const handleMove = (event: globalThis.PointerEvent) => {
      if (isActivePointer(event)) moveTo(event.clientY)
    }
    const handleUp = (event: globalThis.PointerEvent) => {
      if (isActivePointer(event)) endDrag()
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)

    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
    }
  }, [isDragging, moveTo, endDrag])

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null

      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowRight':
          next = stepValue(value, 1, activeScale)
          break
        case 'ArrowDown':
        case 'ArrowLeft':
          next = stepValue(value, -1, activeScale)
          break
        case 'PageUp':
          next = snapValue(value + activeScale.stepHigh, activeScale)
          break
        case 'PageDown':
          next = snapValue(value - activeScale.stepHigh, activeScale)
          break
        case 'Home':
          next = activeScale.min
          break
        case 'End':
          next = activeScale.max
          break
        default:
          return
      }

      event.preventDefault()
      if (next !== value) {
        onChange(next)
        keyboardDirtyRef.current = true
      }
    },
    [value, activeScale, onChange]
  )

  // Commit (analytics) once the keyboard interaction settles, on blur.
  const onBlur = useCallback(() => {
    if (keyboardDirtyRef.current) {
      keyboardDirtyRef.current = false
      onCommit?.(valueRef.current)
    }
  }, [onCommit])

  // Water surface position (band %): follow the pointer while dragging, else the snapped value.
  const valueVisualPct = minVisualPct + (valueToPositionPct(value, activeScale) * (maxVisualPct - minVisualPct)) / 100
  const visualPct = dragVisualPct ?? valueVisualPct

  return { trackRef, isDragging, visualPct, onPointerDown, onKeyDown, onBlur }
}
